import { HaircutType, InflightNegotiations, NegotiationFieldMeta } from 'api'
import { Locale, Translation } from 'locale'
import { isEqual } from 'lodash'
import { getNegotiatedValue } from 'model'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { hasCurrentOrganisationGridViewEnabledSelector } from 'selectors/organisationSelectors'
import { aggregateAllOrNothing, useAppDispatch } from 'utils'
import { DataGrid, DefaultPlaceholder, GridId } from 'view/components/datagrid'
import { useIsAdmin } from 'view/hooks'
import { CenterPanel } from 'view/routes/routesLayout'
import { InitiatedGridViewId } from './InitiatedGridViewColumnNames'
import { initiatedGridViewColumnDefsSelector, initiatedGridViewGridOptions } from './InitiatedGridViewGridModel'
import { InitiatedGridViewHeroPanel } from './InitiatiedGridViewHeroPanel'
import { NegotiationsBulk } from './NegotiationsBulkThunks'
import { counterpartyColumnDetailSelector, initiatedInflightDetailsSelector } from './selectors'
import { InflightTargetThunks } from './TargetInflightThunks'
import { useGridViewContextMenu } from './useGridViewContextMenu'
import { AggregatedPartialNegotiationDetails, isAggregatedDetail, isInflight } from './utils'

export const InitiatedGridViewScreen = () => {
  const inflights = useSelector(initiatedInflightDetailsSelector)
  const dispatch = useAppDispatch()
  const colDefs = useSelector(initiatedGridViewColumnDefsSelector)
  const counterpartyCols = useSelector(counterpartyColumnDetailSelector)
  const contextMenu = useGridViewContextMenu()
  const isAdmin = useIsAdmin()
  const isGridViewEnabled = useSelector(hasCurrentOrganisationGridViewEnabledSelector)

  const key = useMemo(() => counterpartyCols.map(c => c.id).join(','), [counterpartyCols])
  const canEditRow = useCallback(() => !isAdmin, [isAdmin])

  const onCellValueUpdated = useCallback(
    (
      nodeId: string,
      data: InflightNegotiations | AggregatedPartialNegotiationDetails,
      fieldName: string,
      value: string,
      rawValue: string
    ) => {
      if (fieldName === 'target' && isInflight(data)) {
        typeof value === 'number' &&
          data.collateral?.id &&
          data.direction &&
          dispatch(
            InflightTargetThunks.update({
              collateralId: data.collateral?.id,
              direction: data.direction,
              target: value
            })
          )
        return
      }

      if (nodeId.includes(InitiatedGridViewId.COUNTERPARTY_RATE) && isAggregatedDetail(data) && typeof value === 'number') {
        dispatch(
          NegotiationsBulk.update({
            negotiationIds: data.negotiationIds,
            updatedAttributes: {
              [NegotiationFieldMeta.RATE]: [value, rawValue]
            }
          })
        )
      }

      if (nodeId.includes(InitiatedGridViewId.COUNTERPARTY_HAIRCUT) && isAggregatedDetail(data) && typeof value === 'number') {
        const haircutType = getNegotiatedValue(data.negotiation.haircutType) ?? HaircutType.HAIRCUT

        dispatch(
          NegotiationsBulk.update({
            negotiationIds: data.negotiationIds,
            updatedAttributes: {
              [NegotiationFieldMeta.HAIRCUT]: [value, rawValue],
              [NegotiationFieldMeta.HAIRCUT_TYPE]: [haircutType, haircutType]
            }
          })
        )
      }
    },
    [dispatch]
  )

  return (
    <CenterPanel>
      <InitiatedGridViewHeroPanel />
      <DataGrid
        getContextMenuItems={contextMenu}
        gridId={GridId.GridView}
        key={key}
        disableGrouping={true}
        disableFilters={true}
        trackGridRowInEdit={true}
        shouldStopEditOnRowDataUpdate={shouldStopEditOnRowDataUpdate}
        placeholder={isAdmin && !isGridViewEnabled ? GridViewAdminPlaceholder : undefined}
        canEditRow={canEditRow}
        onCellValueUpdated={onCellValueUpdated}
        gridClass="initiated-grid-view"
        gridOptions={initiatedGridViewGridOptions}
        rowData={inflights}
        columnDefs={colDefs}
      />
    </CenterPanel>
  )
}

const GridViewAdminPlaceholder = () => (
  <DefaultPlaceholder>{Locale.translate(Translation.SC_NOT_AVAILABLE_FOR_THIS_PROFILE)}</DefaultPlaceholder>
)

const shouldStopEditOnRowDataUpdate = (prev: InflightNegotiations | null, next: InflightNegotiations | null) => {
  const getStatuses = (inflights: InflightNegotiations) =>
    inflights?.negotiationsByCounterparty &&
    Object.values(inflights?.negotiationsByCounterparty).flatMap(details => aggregateAllOrNothing(details, d => d.negotiation.status))

  return !!(prev && next && !isEqual(getStatuses(prev), getStatuses(next)))
}
